import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";

const columns = [
  { key: "name", label: "Nombre", width: "minmax(240px, 2.3fr)" },
  { key: "sector", label: "Sector", width: "minmax(105px, 0.9fr)" },
  { key: "resultUnit", label: "Unidad de medida", width: "minmax(125px, 1.15fr)" },
  { key: "yieldFactor", label: "Rendimiento", width: "minmax(105px, 0.9fr)" },
  { key: "generatedQuantity", label: "Cantidad generada", width: "minmax(125px, 1.1fr)" },
  { key: "recipes", label: "Receta elaborados", width: "minmax(120px, 1fr)" },
  { key: "status", label: "Estado", width: "minmax(95px, 0.8fr)" },
  { key: "actions", label: "Acciones", width: "minmax(80px, 0.65fr)" },
];

const tableGridTemplate = columns.map((column) => column.width).join(" ");

const gridSx = {
  display: "grid",
  gridTemplateColumns: tableGridTemplate,
  columnGap: 0.75,
  alignItems: "center",
  "& > *": { minWidth: 0 },
};

function TextCell({ children }) {
  return (
    <Typography
      noWrap
      title={typeof children === "string" ? children : undefined}
      sx={{
        color: "primary.900",
        fontSize: 14,
        fontWeight: 600,
        textAlign: "center",
      }}
    >
      {children}
    </Typography>
  );
}

function StatusPill({ active }) {
  return (
    <Box
      sx={{
        width: 74,
        px: 0.8,
        py: 0.55,
        borderRadius: "20px",
        bgcolor: active ? "rgba(33, 150, 83, 0.16)" : "rgba(195, 60, 60, 0.16)",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.28)",
      }}
    >
      <Typography
        sx={{
          color: active ? "rgba(18, 111, 63, 1)" : "rgba(160, 49, 49, 1)",
          fontSize: 12,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        {active ? "Activo" : "Inactivo"}
      </Typography>
    </Box>
  );
}

export default function ElaboradosTable({
  rows = [],
  loading = false,
  onViewRecipe,
  onEdit,
  emptyMessage = "Todavía no hay elaborados cargados.",
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        borderRadius: "20px",
        overflow: "hidden",
      }}
    >
      <Box sx={{ height: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <Box
          sx={{
            minWidth: 1090,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              ...gridSx,
              bgcolor: "neutral.600",
              flexShrink: 0,
              overflowY: "hidden",
              scrollbarGutter: "stable",
              px: 2,
              py: 1.6,
            }}
          >
            {columns.map((column) => (
              <Typography
                key={column.key}
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "logoblanco",
                  lineHeight: 1.15,
                  textAlign: "center",
                }}
              >
                {column.label}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              bgcolor: "neutral.50",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              scrollbarGutter: "stable",
            }}
          >
            {rows.map((preparedProduct) => (
              <Box
                key={preparedProduct.id}
                sx={{
                  px: 2,
                  py: 1.2,
                  borderBottom: "1px solid rgba(0,0,0,.06)",
                  minHeight: 58,
                  "&:hover": { bgcolor: "rgba(0,0,0,.03)" },
                }}
              >
                <Box sx={gridSx}>
                  <Box>
                    <TextCell>{preparedProduct.name}</TextCell>
                  </Box>
                  <Box>
                    <TextCell>{preparedProduct.sectorLabel}</TextCell>
                  </Box>
                  <Box>
                    <TextCell>{preparedProduct.resultUnitLabel}</TextCell>
                  </Box>
                  <Box>
                    <TextCell>{preparedProduct.yieldLabel}</TextCell>
                  </Box>
                  <Box>
                    <TextCell>{preparedProduct.generatedQuantityLabel}</TextCell>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button
                      size="small"
                      disabled={loading || !preparedProduct.hasRecipe}
                      onClick={() => onViewRecipe?.(preparedProduct)}
                      sx={{
                        width: 82,
                        minWidth: 82,
                        py: 0.55,
                        px: 0.8,
                        borderRadius: "20px",
                        bgcolor: "secondary.500",
                        color: "logoblanco",
                        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.35)",
                        textTransform: "none",
                        "&:hover": { bgcolor: "secondary.600" },
                        "&.Mui-disabled": {
                          bgcolor: "neutral.300",
                          color: "neutral.700",
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                        Ver más
                      </Typography>
                    </Button>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <StatusPill active={preparedProduct.isActive} />
                  </Box>
                  <Box>
                    <Stack direction="row" spacing={1} sx={{ justifyContent: "center" }}>
                      <Tooltip
                        title="Editar elaborado"
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={loading}
                            onClick={() => onEdit?.(preparedProduct)}
                            aria-label={`Editar ${preparedProduct.name}`}
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: "neutral.50",
                              boxShadow: "0px 2px 8px rgba(0,0,0,.08)",
                              "&:hover": { bgcolor: "secondary.100" },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 18, color: "primary.900" }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            ))}

            {rows.length === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography sx={{ color: "neutral.700", fontWeight: 600 }}>
                  {loading ? "Cargando elaborados..." : emptyMessage}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
